import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { getDashboardSaleReportSummary } from "../../../services";
import {
  DashboardSaleReportSubmission,
  PaginatedDashboardSaleReportSubmission,
} from "../../../types/dashboard.type";
import {
  DashboardPeriod,
  PERIOD_NOUN_LABEL,
  PeriodRange,
} from "../../../utils/dashboardPeriod";
import { formatNumber, numberWithCommas } from "../../../utils";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import StatusChip from "../StatusChip";
import ReportPagination from "../../Report/ReportList/Pagination";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";
import {
  DEPOSIT_REPORT_PARAM,
  branchDayDetailUrl,
  saleDepositSummaryUrl,
} from "../../SaleDepositReport/depositReportUrl";

const compact = "@media " + COMPACT_QUERY;

const COLUMN_COUNT = 7;

const API_DATE_FORMAT = "YYYY-MM-DD";

interface SaleReportSubmissionProps {
  dateRange: PeriodRange;
  branchCode: string;
  period: DashboardPeriod;
}

const headCellSx = { color: "#aeaeae", fontWeight: 600 } as const;

const renderSkeletonRows = () =>
  Array.from({ length: 10 }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: COLUMN_COUNT }).map((__, colIndex) => (
        <TableCell key={colIndex} align="center">
          <Skeleton variant="text" />
        </TableCell>
      ))}
    </TableRow>
  ));

export default function SaleReportSubmission({
  dateRange,
  branchCode,
  period,
}: SaleReportSubmissionProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [rows, setRows] = useState<DashboardSaleReportSubmission[]>([]);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);

  const onGetReportSummary = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const {
        data: { data },
      } = await getDashboardSaleReportSummary(
        dateRange.date_from,
        dateRange.date_to,
        page,
        limit,
        undefined,
        branchCode,
      );
      const payload = data as PaginatedDashboardSaleReportSubmission;
      setRows(payload.rows ?? []);
      setCount(payload.count ?? 0);
    } catch {
      setError(true);
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [dateRange.date_from, dateRange.date_to, page, limit, branchCode]);

  useEffect(() => {
    onGetReportSummary();
  }, [onGetReportSummary]);

  useEffect(() => {
    setPage(1);
  }, [dateRange.date_from, dateRange.date_to, branchCode]);

  /**
   * Drill-down to รายงานการโอน, preset to this row's route.
   *
   * The row already names one branch and one business day — the very date printed under its
   * report code, and the branch-day the report's detail screen is addressed by — so it opens
   * that screen directly with the route in the query string for the table to filter on.
   * /report/deposit itself is no longer the route-level table for this audience: head-office
   * accounting, the only cohort this dashboard is shown to, lands on the branch-day summary
   * there, which knows nothing about a single route and would drop what was clicked.
   *
   * The section's own date window rides along as the return ticket, so ย้อนกลับ arrives on
   * the summary over the period being reviewed rather than on a reset screen.
   */
  const goToDepositReport = (row: DashboardSaleReportSubmission) => {
    const params = new URLSearchParams();
    params.append(DEPOSIT_REPORT_PARAM.dateFrom, dateRange.date_from);
    params.append(DEPOSIT_REPORT_PARAM.dateTo, dateRange.date_to);
    if (row.route) params.append(DEPOSIT_REPORT_PARAM.routeCode, row.route);
    if (row.branch_code)
      params.append(DEPOSIT_REPORT_PARAM.branchCode, row.branch_code);

    // A row missing either half of its branch-day cannot address the detail screen; the
    // summary over the same window is the closest thing to what was asked for.
    const businessDate = dayjs(row.date);
    const search = params.toString();
    navigate(
      row.branch_code && businessDate.isValid()
        ? branchDayDetailUrl(
            row.branch_code,
            businessDate.format(API_DATE_FORMAT),
            search,
          )
        : saleDepositSummaryUrl(search),
    );
  };

  return (
    <Paper sx={{ padding: 0 }}>
      <Typography
        sx={{ padding: "16px 16px 0" }}
        variant="body1"
        mb={2}
        fontWeight={600}
      >
        ข้อมูลการส่งรายงานประจำ{PERIOD_NOUN_LABEL[period]}
      </Typography>
      <TableContainer>
        <Table>
          <TableHead
            sx={{ bgcolor: "#f4f4f4", "& td, & th": { padding: "8px" } }}
          >
            <TableRow>
              <TableCell align="center" sx={headCellSx}>
                ลำดับ
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                สาขา
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                รายงาน
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                จำนวน invoice
              </TableCell>
              <TableCell
                align="center"
                sx={{ ...headCellSx, minWidth: 120, whiteSpace: "nowrap" }}
              >
                ยอดโอน
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                จำนวนรายงานที่ต้องแก้ไข
              </TableCell>
              <TableCell align="center" sx={headCellSx}>
                สถานะ
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && renderSkeletonRows()}

            {!loading && error && (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  align="center"
                  sx={{
                    height: "200px",
                    [compact]: { height: "120px" },
                    verticalAlign: "middle",
                  }}
                >
                  <Typography color="error" fontWeight={500}>
                    ไม่สามารถโหลดข้อมูลได้
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading && !error && rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT}
                  align="center"
                  sx={{
                    height: "200px",
                    [compact]: { height: "120px" },
                    verticalAlign: "middle",
                  }}
                >
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูล
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              !error &&
              rows.map((row, index) => (
                <TableRow
                  key={row.sale_report_code + index}
                  sx={{ "& td, & th": { padding: "8px" } }}
                >
                  <TableCell align="center">
                    {(page - 1) * limit + index + 1}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {row.branch_name || row.branch_code || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box>
                      <Typography fontWeight={500} color="#333">
                        {row.sale_report_code}
                      </Typography>
                      {row.date && (
                        <Typography variant="caption" color="text.secondary">
                          {row.date}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography component="span" fontWeight={500} color="#333">
                      {numberWithCommas(row.bill_count)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    {row.transfer_amount == null ? (
                      <Typography variant="body2" color="text.secondary">
                        ยังไม่มียอด
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.25,
                        }}
                      >
                        <Typography fontWeight={500} color="#333">
                          {formatNumber(row.transfer_amount)}
                        </Typography>
                        <Typography
                          variant="caption"
                          onClick={() => goToDepositReport(row)}
                          sx={{
                            color: "link.main",
                            fontWeight: 600,
                            cursor: "pointer",
                            textDecoration: "underline",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          ดูการโอน
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight={500}>
                      {row.bill_count_request_change || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center">
                      <StatusChip status={row.status} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!error && (
        <ReportPagination
          totalCount={count}
          page={page}
          setPage={setPage}
          limit={limit}
          setLimit={setLimit}
        />
      )}
    </Paper>
  );
}
